import { useState, useMemo } from "react";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import Spinner from "./common/Spinner";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Input } from "../components/common/Input";
import * as utils from "../utils/utils";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { formatDateDMY } from "../lib/ScheduleUtils";
import { getLocalizedDayName, getLocalizedAppointmentType } from "../i18n/scheduleLabels";
import useBrandTranslation from "../hooks/useBrandTranslation";

import {
  Search,
  Stethoscope,
  Calendar,
  Users,
  ArrowLeft,
  User,
  Mail,
  Phone,
  Clock,
  ChevronRight,
} from "lucide-react";

export default function SecretaryDoctorSelection() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const {
    schedules,
    sharedSchedules,
    getSharedSchedulesBySecretary,
    getDoctorsForSecretary,
    loadData,
  } = useData();
  const navigate = useNavigate();

  usePeriodicLoadData(
    loadData,
    undefined,
    ["secretary", "institution"].includes(user?.role)
  );
  const [searchTerm, setSearchTerm] = useState("");

  // Obtener horarios compartidos (secretaria o institución)
  const mySharedSchedules =
    ["secretary", "institution"].includes(user?.role)
      ? getSharedSchedulesBySecretary(user.id)
      : [];

  const associatedDoctors =
    ["secretary", "institution"].includes(user?.role)
      ? getDoctorsForSecretary(user.id)
      : [];

  // Obtener médicos únicos que han compartido horarios
  const availableDoctors = useMemo(() => {
    if (!associatedDoctors.length) return [];

    return associatedDoctors
      .map((doctor) => {
        const sharedEntries = mySharedSchedules.filter((shared) => {
          const schedule = schedules.find(
            (scheduleItem) => scheduleItem.id === shared.schedule_id
          );
          return schedule?.doctor_id === doctor.id;
        });

        const doctorSchedules = sharedEntries
          .map((shared) =>
            schedules.find((scheduleItem) => scheduleItem.id === shared.schedule_id)
          )
          .filter(Boolean);

        const sharedCount = doctorSchedules.length;
        const lastShared =
          sharedEntries.length > 0
            ? Math.max(
                ...sharedEntries.map((entry) => {
                  const created = entry.created_at
                    ? new Date(entry.created_at).getTime()
                    : 0;
                  return created;
                })
              )
            : 0;

        return {
          ...doctor,
          sharedCount,
          sharedSchedules: doctorSchedules,
          lastShared,
        };
      })
      .sort((a, b) => b.lastShared - a.lastShared);
  }, [associatedDoctors, mySharedSchedules, schedules]);

  // Filtrar médicos por búsqueda
  const filteredDoctors = useMemo(() => {
    if (!searchTerm.trim()) return availableDoctors;

    const term = searchTerm.toLowerCase().trim();
    return availableDoctors.filter(
      (doctor) =>
        doctor.name.toLowerCase().includes(term) ||
        doctor.last_name.toLowerCase().includes(term) ||
        (doctor.specialization &&
          doctor.specialization.toLowerCase().includes(term)) ||
        doctor.email.toLowerCase().includes(term)
    );
  }, [availableDoctors, searchTerm]);

  const handleSelectDoctor = (doctor) => {
    // Navegar a los horarios compartidos con el médico preseleccionado
    navigate(`/secretary/schedule?doctorId=${doctor.id}`);
  };

  const getDayName = (dayOfWeek) => getLocalizedDayName(dayOfWeek);

  const getScheduleSummary = (doctor) => {
    const days = new Set();
    const types = new Set();

    doctor.sharedSchedules.forEach((schedule) => {
      if (schedule) {
        days.add(getDayName(schedule.day_of_week));
        types.add(getLocalizedAppointmentType(schedule.appointment_type));
      }
    });

    return {
      days: Array.from(days).slice(0, 3), // Mostrar máximo 3 días
      types: Array.from(types),
      totalDays: days.size,
    };
  };

  if (!user) return <Spinner className="w-10 h-10 mx-auto" />;

  if (user && !["secretary", "institution"].includes(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Esta sección es solo para secretarias.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Seleccionar Médico
          </h1>
          <p className="text-gray-600">
            Elige el médico con el que quieres trabajar hoy
          </p>
        </div>

        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {availableDoctors.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.secretary.availableDoctors", "Médicos Disponibles")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {mySharedSchedules.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.secretary.sharedSchedules", "Horarios Compartidos")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-purple-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {
                      new Set(
                        availableDoctors
                          .map((d) => d.specialization)
                          .filter(Boolean)
                      ).size
                    }
                  </p>
                  <p className="text-sm text-gray-600">{t("app.secretary.specialties", "Especialidades")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Barra de búsqueda */}
        {availableDoctors.length > 0 && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder={t("app.secretary.searchDoctor", "Buscar médico por nombre, apellido, especialidad...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 h-12 text-base"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de médicos */}
        <div className="space-y-4 mb-20">
          {/* Contador de resultados */}
          {availableDoctors.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                {filteredDoctors.length} médico
                {filteredDoctors.length !== 1 ? "s" : ""} disponible
                {filteredDoctors.length !== 1 ? "s" : ""}
                {searchTerm && ` para "${searchTerm}"`}
              </p>
            </div>
          )}

          {/* Contenido principal */}
          {availableDoctors.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No hay médicos disponibles
                </h3>
                <p className="text-gray-500 mb-4">
                  Aún no tienes médicos que hayan compartido sus horarios
                  contigo.
                </p>
                <p className="text-sm text-gray-400">
                  Los médicos pueden compartir sus horarios desde su panel de
                  control.
                </p>
              </CardContent>
            </Card>
          ) : filteredDoctors.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No se encontraron médicos
                </h3>
                <p className="text-gray-500 mb-4">
                  No hay médicos que coincidan con tu búsqueda "{searchTerm}".
                </p>
                <Button variant="outline" onClick={() => setSearchTerm("")}>
                  Limpiar búsqueda
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ marginBottom: "100px !important" }}>
              {filteredDoctors.map((doctor) => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  onSelect={handleSelectDoctor}
                  getScheduleSummary={getScheduleSummary}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DoctorCard({ doctor, onSelect, getScheduleSummary }) {
  const summary = getScheduleSummary(doctor);

  return (
    <Card
      className="hover:shadow-lg transition-all duration-200 cursor-pointer group"
      onClick={() => onSelect(doctor)}
    >
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-lg group-hover:text-blue-600 transition-colors">
              <User className="w-5 h-5 text-blue-600" />
              Dr. {doctor.name} {doctor.last_name}
            </CardTitle>

            {doctor.specialization && (
              <Badge variant="outline" className="mt-2">
                <Stethoscope className="w-3 h-3 mr-1" />
                {doctor.specialization}
              </Badge>
            )}
          </div>

          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Información de contacto */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Mail className="w-4 h-4" />
            <span>{doctor.email}</span>
          </div>

          {doctor.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="w-4 h-4" />
              <span>{doctor.phone}</span>
            </div>
          )}
        </div>

        {/* Resumen de horarios compartidos */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Horarios Compartidos
          </h4>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-blue-800">
                {doctor.sharedCount}
              </span>
              <span className="text-blue-700">
                horario{doctor.sharedCount !== 1 ? "s" : ""} disponible
                {doctor.sharedCount !== 1 ? "s" : ""}
              </span>
            </div>

            {summary.days.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {summary.days.map((day, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {day}
                  </Badge>
                ))}
                {summary.totalDays > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{summary.totalDays - 3} más
                  </Badge>
                )}
              </div>
            )}

            {summary.types.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {summary.types.map((type, index) => (
                  <Badge
                    key={index}
                    variant={
                      type === "Particular"
                        ? "appointmentPrivate"
                        : "appointmentInsurance"
                    }
                    className="text-xs"
                  >
                    {type}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Información adicional */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          <span>
            Último horario compartido:{" "}
            {formatDateDMY(doctor.lastShared)}
          </span>
        </div>

        {/* Botón de acción */}
        <Button className="w-full group-hover:bg-blue-600 transition-colors">
          <Calendar className="w-4 h-4 mr-2" />
          Ver Horarios y Asignar Turnos
        </Button>
      </CardContent>
    </Card>
  );
}
